import { pubSub } from '@utils/PubSub';
import {
  captureUserActionError,
  USER_ACTIONS,
  withSentrySpan,
} from '@utils/sentry';

interface DYRecommendationContext {
  type: 'CART' | 'PRODUCT' | 'CATEGORY' | 'HOMEPAGE' | 'OTHER';
  data?: string[];
}

declare global {
  interface Window {
    DY?: { recommendationContext?: DYRecommendationContext };
  }
}

export default class Cart {
  private dyRecommendationContextOriginal: DYRecommendationContext | undefined =
    undefined;
  private cartFlyoutActive = false;

  constructor() {
    window.addEventListener('init-overlay', async (e: Event) => {
      if ((e as CustomEvent).detail !== 'cart') return;

      //Because the cart is a flyout, we must store the original value of DY's recommendation context so we can revert back to the original page context if the cart is closed.
      //Only capture the original context on the first open — subsequent init-overlay events are cart refreshes where the context is already CART.
      if (!this.cartFlyoutActive) {
        this.dyRecommendationContextOriginal = window.DY?.recommendationContext;
      }
      this.cartFlyoutActive = true;
      await this.setDYContext();
      pubSub.publish('theme:cart:upsell:fetch');
    });

    window.addEventListener('close-overlay', (e: Event) => {
      if ((e as CustomEvent).detail !== 'cart') return;
      this.cartFlyoutActive = false;
      if (window.DY) {
        //revert from cart recommendation context to original page's recommendation context
        window.DY.recommendationContext = this.dyRecommendationContextOriginal;
        this.updateDYContextScript(this.dyRecommendationContextOriginal);
      }
    });

    document.addEventListener('theme:add-to-cart', async (e) => {
      const item = e.detail;
      const hasGiftKey = item?.properties
        ? Object.keys(item.properties).includes('_isGiftCard')
        : false;
      const isGiftCard = hasGiftKey
        ? JSON.parse(item?.properties?._isGiftCard)
        : false;
      const itemInCart = await this.itemPresentInCart(item);
      const urgencySet =
        JSON.parse(sessionStorage.getItem('urgencySet')) ?? false;
      const urgencyEnabled =
        JSON.parse(sessionStorage.getItem('urgencyEnabled')) ?? false;
      const notRemoving = item.quantity > 0;

      if (!urgencySet && urgencyEnabled && notRemoving) {
        this.emitUrgencyEvent();
      }

      if (itemInCart && !isGiftCard) {
        this.updateCart(item);
      } else {
        this.addToCart(item);
      }
    });

    //YOTPO REWARDS CART TRIGGER
    if (
      window.location.href.includes('/rewards') ||
      window.location.href.includes('/account')
    ) {
      document.addEventListener('click', (e) => {
        const yotpoButtonText = e.target.parentNode
          .getAttribute('aria-label')
          ?.toLowerCase();

        if (yotpoButtonText == 'redeem') {
          //yotpo does not have any specific class identifiers for the different states of redemption buttons, nor their text containers. As a result, we must rely on aria label attributes and inner text to specifically target elements in the widget
          //when a user redeems a rewards product via Yotpo's 'redeem' button, fire a mutation observer that checks when a user has successfully added a reward product to their cart
          //when a product has been successfully added to the cart, update the flyout and trigger the opening

          // Select the node that will be observed for mutations
          const targetNodes = document.querySelectorAll(
            '.yotpo-redemption-option'
          );

          // Options for the observer (which mutations to observe)
          const config = { childList: true, subtree: true };

          // Callback function to execute when mutations are observed
          const callback = (mutationList, observer) => {
            for (const mutation of mutationList) {
              if (
                mutation.addedNodes?.[0]?.innerText?.includes(
                  'Product has been successfully added to your cart'
                )
              ) {
                this.getCartResponse().then((data) => {
                  this.updateCartIconQuantities(data);

                  //A/B test: show the interstitial instead of opening the cart
                  //drawer, same as a regular add to cart (see addToCart above).
                  //There's no /cart/add.js payload to identify the redeemed
                  //reward by id, so flag it for findInterstitialItem to locate
                  //by diffing against the cart's pre-redemption state instead.
                  const onCartPage = document.body?.dataset.template === 'cart';
                  const cartInterstitialActive =
                    window?.Brooklinen?.cart_interstitial === true &&
                    !onCartPage;
                  this.emitFetch(
                    cartInterstitialActive ? 'silent' : 'open',
                    cartInterstitialActive ? { rewardRedemption: true } : null
                  );
                  observer.disconnect();
                });
              }
            }
          };

          const observer = new MutationObserver(callback);

          // Start observing the target node for configured mutations
          targetNodes.forEach((node) => {
            observer.observe(node, config);
          });
        }
      });
    }

    document.addEventListener('theme:clear-cart', () => {
      this.clearCart();
    });

    this.sienaCartUpdate();
  }

  /**
   * Updates window.DY.recommendationContext with the current cart's variant IDs.
   * Only runs while the cart flyout is active.
   */
  async setDYContext() {
    if (!window.DY || !this.cartFlyoutActive) return;

    const { items } = await this.getCartResponse();

    //Pass an array of variantIds to window.DY.recommendationContext. Do not pass free gift item variant IDs, and in the case of checkout bundle items, pass the original bundle variant ID.
    const variantIds =
      items
        ?.filter(
          (item) =>
            !item.properties?._freeGift &&
            !item.properties?._yotpo_loyalty_discount_type
        )
        .map((item) => {
          const parentInstance =
            item.properties?._product_bundle_parent_instance;
          if (!parentInstance) return String(item.id);
          return parentInstance.split('_').at(-1) ?? '';
        }) ?? [];

    window.DY.recommendationContext = { type: 'CART', data: variantIds };
    this.updateDYContextScript(window.DY.recommendationContext);
    window.dispatchEvent(new CustomEvent('dy:context:set'));
  }

  //In addition to the window object, we must also update the DOM so DY's native mutation observers run properly when dynamically updating page context
  updateDYContextScript(context: DYRecommendationContext | undefined) {
    const script = document.querySelector('#dy-recommendation-context');
    if (!script || !context) return;
    script.textContent = `window.DY = window.DY || {}; DY.recommendationContext = ${JSON.stringify(
      context
    )};`;
  }

  async getCartResponse(): Promise<{
    items?: Array<{
      id: number;
      key: string;
      quantity: number;
      properties?: Record<string, string>;
    }>;
  }> {
    const cart = await fetch('/cart.json');
    return cart.json();
  }

  sienaCartUpdate() {
    document.body.addEventListener('siena:cart:updated', (e: Event) => {
      const eventDetails = (e as CustomEvent).detail;
      this.updateCartIconQuantities(eventDetails.cart);
      this.emitFetch();
      this.setDYContext();

      try {
        window.heap.track('Theme - Siena - Add to Cart');
      } catch (e) {
        throw new Error(`Heap error: ${e}`);
      }
    });
  }

  async itemPresentInCart(payload) {
    const { items } = await this.getCartResponse();
    const matches = this.findCartItem(payload, items);
    return matches.length > 0;
  }

  findCartItem(payload, cartItems) {
    return cartItems.filter((item) => {
      const hasMatchingId = item.id.toString() === payload.id;
      const hasMatchingKey = item.key === payload.id;
      return hasMatchingId || hasMatchingKey;
    });
  }

  /**
   * Notifies the Vue cart app that the underlying cart changed.
   * @param {'open' | 'silent'} mode - 'open' refreshes cart state and opens the drawer (default,
   * current behavior). 'silent' refreshes cart state without opening the drawer — used when the
   * cart interstitial A/B variant is active, so it can locate and display the just-added item instead.
   * @param {object|null} formData - the payload that was just POSTed to /cart/add.js, used by the
   * Vue cart app to identify which item(s) to show in the interstitial.
   */
  emitFetch(mode: 'open' | 'silent' = 'open', formData = null) {
    return pubSub.publish('cart:request', { mode, formData });
  }

  emitUrgencyEvent() {
    const now = new Date();
    sessionStorage.setItem('urgencySet', true);
    sessionStorage.setItem('urgencyStart', now);
    pubSub.publish('cart:urgency:start');
  }

  async addToCart(formData) {
    // AutoBundle and SteppedBundle share the same items payload, so this
    // intentionally tracks both bundle types without distinguishing them.
    const isBundle = Array.isArray(formData?.items);

    try {
      const response = await withSentrySpan(
        {
          name: 'add_to_cart',
          op: 'ui.action',
        },
        async (span) => {
          span?.setAttribute?.('bundle.is_bundle', isBundle);

          if (isBundle) {
            span?.setAttribute?.(
              'bundle.expected_line_items',
              formData.items.length
            );
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          });

          span?.setAttribute?.('http.status_code', response.status);

          span?.setStatus?.({
            code: response.ok ? 1 : 2,
          });
          return response;
        }
      );

      // fetch only rejects on network failure, not on HTTP 4xx/5xx, so an error
      // status (e.g. 422 sold out / quantity limit) must be checked explicitly.
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          `Add to cart failed (POST /cart/add.js, ${response.status}): ${
            body?.description ?? body?.message ?? response.statusText
          }`
        );
      }

      const cart = await fetch('/cart.js').then((res) => res.json());
      this.updateCartIconQuantities(cart);

      //A/B test: show a confirmation popup instead of opening the cart drawer.
      // Skip it on the cart page itself — the user is already looking at their
      // cart, so popping up a summary of what's in it on top is redundant.
      const onCartPage = document.body?.dataset.template === 'cart';
      const cartInterstitialActive =
        window?.Brooklinen?.cart_interstitial === true && !onCartPage;
      this.emitFetch(cartInterstitialActive ? 'silent' : 'open', formData);
      this.setDYContext();
    } catch (error) {
      captureUserActionError(USER_ACTIONS.ADD_TO_CART, error);
      // Lets ATC buttons (e.g. ProductActions.vue's loading state) know the
      // add failed, so they don't sit showing "Adding..." forever — nothing
      // else fires on this path since neither emitFetch (cart opened) nor
      // the interstitial's own event ever runs when /cart/add.js fails.
      window.dispatchEvent(new CustomEvent('cart:add-to-cart:error'));
    }
  }

  async updateCart(payload) {
    const quantity = Number(payload?.quantity);
    const mutation = quantity === 0 ? 'remove' : 'quantity_update';
    const request = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await withSentrySpan(
        {
          name: 'update_cart',
          op: 'ui.action',
        },
        async (span) => {
          span?.setAttribute?.('cart.mutation', mutation);
          span?.setAttribute?.('cart.quantity', quantity);

          const response = await fetch('/cart/change.js', request);
          span?.setAttribute?.('http.status_code', response.status);
          span?.setStatus?.({
            code: response.ok ? 1 : 2,
          });

          return response;
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          `Update cart failed (POST /cart/change.js, ${response.status}): ${
            body?.description ?? body?.message ?? response.statusText
          }`
        );
      }

      const cart = await response.json();
      this.updateCartIconQuantities(cart);
      this.emitFetch();
      this.setDYContext();
    } catch (error) {
      captureUserActionError(USER_ACTIONS.UPDATE_CART, error);
      // theme:add-to-cart routes here instead of addToCart() when the item is
      // already in the cart — same "Adding..." stuck-loading risk on failure.
      window.dispatchEvent(new CustomEvent('cart:add-to-cart:error'));
    }
  }

  clearCart() {
    fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        this.updateCartIconQuantities();
        this.setDYContext();
        return response.json();
      })
      .catch((error) => {
        throw new Error(`Clear cart error, ${error}`);
      });
  }

  updateCartIconQuantities(cart?: { items? }) {
    let itemCount = 0;
    if (cart?.items) {
      cart.items.forEach((item) => {
        const included =
          !item?.properties?._freeGift &&
          !item?.properties?._yotpo_loyalty_discount_type;
        if (included) itemCount = itemCount + item.quantity;
      });
    }

    document.querySelectorAll('.header__icon--cart').forEach((icon) => {
      itemCount > 0
        ? icon.querySelector('svg').classList.add('cart-populated')
        : icon.querySelector('svg').classList.remove('cart-populated');
      icon
        .querySelector('.cart-count-bubble')
        .setAttribute('data-cart-size', `${itemCount}`);
      icon.querySelector('span[aria-hidden="true"]').innerHTML = `${itemCount}`;
    });
  }
}
